import {
  Button,
  colors,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Grid,
  Switch,
  TextField,
  Theme,
  Typography
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import 'graphiql/graphiql.css';
import { buildClientSchema, getIntrospectionQuery, GraphQLSchema } from 'graphql';
import { useSnackbar } from 'notistack';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import useReactRouter from 'use-react-router';
import uuidv4 from 'uuid/v4';
import validate from 'validate.js';
import { TableFromJson } from '../../../../components/TableFromJson';
import { getMe } from '../../../../graphql/auth';
import { getHeaders, graphQlUrl } from '../../../../graphql/GraphQlProvider';
import { Report } from '../../../../graphql/reports.datatypes';
import flattenJson from '../../../../utils/flattenJson';
import createReport, { CreateReportVars } from './createReport.api';
import './graphiql.css';
import updateReport, { UpdateReportVars } from './updateReport.api';

const gql = require('graphql-tag');

const GraphiQL = require('graphiql').default;
const GraphiQLExplorer = require('graphiql-explorer').default;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  graphiql: {
    height: 'calc(80vh - 94px)',
    border: 'grey 1px solid',
    borderColor: theme.palette.grey[300],
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  jsonTable: {
    width: '100%',
  },
  container: {
    marginTop: theme.spacing(3),
    width: '100%',
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  tablePanel: {
    padding: 0,
  }
}));

interface GraphIQLRef {
  getQueryEditor: () => any;
  handleRunQuery: (query: string) => any;
  handleToggleHistory: () => any;
  handlePrettifyQuery: () => any;
}

const ReportQueryBuilder = (props: {report: Report}) => {
  const classes = useStyles();
  const graphiqlRef = useRef(null) as unknown as GraphIQLRef;
  const {history}: RouteComponentProps = useReactRouter();
  const isNewReport = props.report.id === -1;

  const [schema, setSchema] = useState<GraphQLSchema|null>(null);
  const [explorerIsOpen, setExplorerIsOpen] = useState(true);
  const [responseJson, setResponseJson] = useState(null);
  const [expanded, setExpanded] = useState<string | false>('queryBuilderPanel');

  const {enqueueSnackbar} = useSnackbar();
  const [sendCreateReport] = createReport();
  const [sendUpdateReport] = updateReport();
  const userData = getMe();

  const [formState, setFormState] = useState({...props.report});
  const [errors, setErrors] = useState({
    isValid: false,
    errors: {
      name: false,
      shared: false,
    }
  });

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    event.persist();
    setFormState((oldState: any) => ({
      ...oldState,
      [event.target.name]:
        event.target.type === 'checkbox'
          ? (event.target as HTMLInputElement).checked
          : event.target.value
    }));
  };

  const hasError = (fieldName: string) => {
    // @ts-ignore
    const foundError = !!errors.errors[fieldName];
    return {
      error: foundError,
      // @ts-ignore
      helperText: foundError ? errors.errors[fieldName]![0] : '',
    };
  };

  const validateSchema = {
    name: {
      presence: {allowEmpty: false, message: 'is required'},
      length: {
        maximum: 64
      }
    },
    query: {
      presence: {allowEmpty: false, message: 'is required'},
    },
  };

  useEffect(() => {
    const newErrors = validate(formState, validateSchema);
    setErrors(state => ({
      ...state,
      isValid: !newErrors,
      errors: newErrors || {}
    }));
  }, [formState]);

  useEffect(
    () => {
      if(graphiqlRef) {
        const promise = fetcher({
          query: getIntrospectionQuery()
        });

        if(promise){
          promise.then(result => {
            if(graphiqlRef.getQueryEditor) {
              const editor = graphiqlRef.getQueryEditor();
              editor.setOption('extraKeys', {
                ...(editor.options.extraKeys || {}),
              });
            }
            setSchema( buildClientSchema(result.data) );
            /*
            //@ts-ignore */
            if(!isNewReport) graphiqlRef.current.handleRunQuery('');
          });
        }
      }
    },
    [graphiqlRef],
  );

  const handleEditQuery = (query: string): void => setFormState({...formState, query});

  const handleToggleExplorer = () => setExplorerIsOpen(!explorerIsOpen);

  const fetcher = (params: any) => {

    if(formState.query.indexOf('mutation') !== -1) {
      enqueueSnackbar('This reporting tool is only for queries. Please do not use mutations.', {variant: 'error'});
      return null;
    }

    return fetch(
      graphQlUrl,
      {
        method: 'POST',
        headers: getHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(params)
      }
    )
      .then((response) => response.text())
      .then((responseBody) => {
        try {
          const json = JSON.parse(responseBody);

          if(json.data && !json.data.__schema){
            const queryObj = gql`${formState.query}`;
            const queryName = queryObj.definitions[0].selectionSet.selections[0].name.value;
            let data = (json.data[queryName].data) ? json.data[queryName].data : json.data[queryName];
            data = flattenJson(data);
            setResponseJson(data as any);
            handlePanelChange('resultsPanel')(null, true);
          }

          return json;
        } catch (e) {
          return responseBody;
        }
      });
  };

  const handlePanelChange = (panel: string) => (event: React.ChangeEvent<{}> | null, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const onSaveClick = () => {
    if (isNewReport) {
      const sanitisedData: CreateReportVars = {
        data: {
          ...{...formState, id:undefined},
          url_key: uuidv4(),
          user: {
            connect: userData.data!.me!.id
          }
        }
      };

      sendCreateReport({variables: sanitisedData})
        .then(() => {
          enqueueSnackbar('Report Saved.', {variant: 'success'});
          history.push('/reports');
        })
        .catch(() => {
          enqueueSnackbar('Save Report Failed!', {variant: 'error'});
        });
    } else {
      const sanitisedData: UpdateReportVars = {
        data: {
          ...{...formState, url_key:undefined, __typename: undefined},
        }
      };

      sendUpdateReport({variables: sanitisedData})
        .then(() => {
          enqueueSnackbar('Report Updated.', {variant: 'success'});
        })
        .catch(() => {
          enqueueSnackbar('Report Update Failed!', {variant: 'error'});
        });
    }
  };

  return (
    <Fragment>

        <ExpansionPanel expanded={expanded === 'queryBuilderPanel'} onChange={handlePanelChange('queryBuilderPanel')}>
          <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className={classes.heading}>Query Builder</Typography>
            <Typography className={classes.secondaryHeading}>Create your report here.</Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails className={classes.tablePanel}>
            <div className={clsx(['graphiql-container', classes.graphiql])} >
              <GraphiQLExplorer
                schema={schema}
                query={formState.query}
                onEdit={handleEditQuery}
                onRunOperation={(operationName: string) => graphiqlRef.handleRunQuery(operationName)}
                explorerIsOpen={explorerIsOpen}
                onToggleExplorer={handleToggleExplorer}
              />
              <GraphiQL
                ref={graphiqlRef}
                fetcher={fetcher}
                schema={schema}
                query={formState.query}
                onEditQuery={handleEditQuery}
              >
                <GraphiQL.Toolbar>
                  <GraphiQL.Button
                    onClick={handleToggleExplorer}
                    label="Explorer"
                    title="Toggle Explorer"
                  />
                </GraphiQL.Toolbar>
              </GraphiQL>
            </div>
          </ExpansionPanelDetails>
        </ExpansionPanel>

        <ExpansionPanel expanded={expanded === 'resultsPanel'} onChange={handlePanelChange('resultsPanel')}>
          <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className={classes.heading}>Results</Typography>
            <Typography className={classes.secondaryHeading}>See your results here.</Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails className={classes.tablePanel}>
            <TableFromJson className={classes.jsonTable} json={responseJson} />
          </ExpansionPanelDetails>
        </ExpansionPanel>


        <ExpansionPanel expanded={expanded === 'savePanel'} onChange={handlePanelChange('savePanel')}>
          <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className={classes.heading}>Save and Share</Typography>
            <Typography className={classes.secondaryHeading}>Save your query for future use and sharing.</Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>

            <Grid className={classes.container} container spacing={3}>
              <Grid item md={6} xs={6}>
                <TextField
                  {...hasError('name')}
                  fullWidth
                  label="Name"
                  name="name"
                  onChange={handleFieldChange}
                  value={formState.name}
                  variant="outlined"
                />
              </Grid>

              <Grid item md={1} xs={2}>
                <Typography variant="h5">Share?</Typography>
                <Switch
                  checked={formState.shared}
                  color="secondary"
                  edge="start"
                  name="shared"
                  onChange={handleFieldChange}
                  value={formState.shared}
                />
              </Grid>

              <Grid item md={1} xs={2}>
                <Button className={classes.saveButton} onClick={onSaveClick} variant="contained"
                        disabled={!errors.isValid}>Save</Button>
              </Grid>
            </Grid>

          </ExpansionPanelDetails>
        </ExpansionPanel>

    </Fragment>
  );
};

export default ReportQueryBuilder;
